import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, CardContent, Input } from '@components';
import { useToast } from '../../../contexts/useToast';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { FaqEntryModal } from '../components/FaqEntryModal';
import { FaqEntryStatus } from '../components/FaqEntryStatus';
import {
  useCreateFaqEntry,
  useDeleteFaqEntry,
  useFaqEntries,
  useProcessFaqEntry,
  useUpdateFaqEntry,
} from '../hooks';
import { FaqEntry, FaqEntryInput, KnowledgeBase } from '../types';

const SEARCH_DEBOUNCE_MS = 300;

interface FaqTabProps {
  knowledgeBase: KnowledgeBase;
}

export const FaqTab: React.FC<FaqTabProps> = ({ knowledgeBase }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [editingEntry, setEditingEntry] = useState<FaqEntry | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState<FaqEntry | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const { data: entries = [], isLoading, isError } = useFaqEntries(knowledgeBase.id, search);
  const createEntry = useCreateFaqEntry(knowledgeBase.id);
  const updateEntry = useUpdateFaqEntry();
  const deleteEntry = useDeleteFaqEntry();
  const processEntry = useProcessFaqEntry();

  const openForm = (entry: FaqEntry | null) => {
    setEditingEntry(entry);
    setIsFormOpen(true);
  };

  const handleSubmit = (data: FaqEntryInput) =>
    editingEntry
      ? updateEntry.mutateAsync({ id: editingEntry.id, data })
      : createEntry.mutateAsync(data);

  const handleDelete = async () => {
    if (!deletingEntry) return;
    try {
      await deleteEntry.mutateAsync(deletingEntry.id);
      showToast({ type: 'success', message: t('knowledgeBase.faq.deleted') });
      setDeletingEntry(null);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.faq.deleteFailed') });
    }
  };

  const handleProcess = async (entry: FaqEntry) => {
    try {
      await processEntry.mutateAsync(entry.id);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.faq.processFailed') });
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('knowledgeBase.faq.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('knowledgeBase.faq.description')}
          </p>
        </div>
        <Button
          variant="gradient-multi"
          onClick={() => openForm(null)}
          className="flex items-center gap-2 shrink-0"
        >
          <span className="material-icons">add</span>
          {t('knowledgeBase.faq.addEntry')}
        </Button>
      </div>

      <Input
        type="text"
        placeholder={t('knowledgeBase.faq.search')}
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        className="w-80 h-10"
      />

      <Card>
        <CardContent>
          {isError ? (
            <p className="py-8 text-center text-red-500">{t('knowledgeBase.faq.loadFailed')}</p>
          ) : isLoading ? (
            <p className="py-8 text-center text-gray-500">{t('common.loading')}</p>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center space-y-2">
              <span className="material-icons text-3xl text-gray-400">quiz</span>
              <p className="text-gray-600 dark:text-gray-400">
                {search ? t('knowledgeBase.faq.noResults') : t('knowledgeBase.faq.empty')}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {entries.map(entry => (
                <FaqEntryRow
                  key={entry.id}
                  entry={entry}
                  onEdit={() => openForm(entry)}
                  onDelete={() => setDeletingEntry(entry)}
                  onProcess={() => handleProcess(entry)}
                  isProcessing={processEntry.isPending && processEntry.variables === entry.id}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <FaqEntryModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        entry={editingEntry}
        isSaving={createEntry.isPending || updateEntry.isPending}
      />

      <ConfirmDeleteModal
        isOpen={deletingEntry !== null}
        onClose={() => setDeletingEntry(null)}
        onConfirm={handleDelete}
        title={t('knowledgeBase.faq.deleteEntry')}
        question={t('knowledgeBase.faq.confirmDelete')}
        itemName={deletingEntry?.question ?? ''}
        warning={t('knowledgeBase.faq.deleteWarning')}
        confirmLabel={t('knowledgeBase.faq.deleteEntry')}
        isDeleting={deleteEntry.isPending}
      />
    </div>
  );
};

interface FaqEntryRowProps {
  entry: FaqEntry;
  onEdit: () => void;
  onDelete: () => void;
  onProcess: () => void;
  isProcessing: boolean;
}

const FaqEntryRow: React.FC<FaqEntryRowProps> = ({
  entry,
  onEdit,
  onDelete,
  onProcess,
  isProcessing,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <li className="py-4 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <p className="font-medium text-gray-900 dark:text-white break-words">{entry.question}</p>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={`text-left text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line break-words ${
              isExpanded ? '' : 'line-clamp-2'
            }`}
            title={isExpanded ? t('knowledgeBase.faq.showLess') : t('knowledgeBase.faq.showMore')}
          >
            {entry.answer}
          </button>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="p-1.5 h-8 w-8"
            title={t('knowledgeBase.faq.editEntry')}
          >
            <span className="material-icons text-base text-gray-600 dark:text-gray-400">edit</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1.5 h-8 w-8"
            title={t('knowledgeBase.faq.deleteEntry')}
          >
            <span className="material-icons text-base text-red-600">delete</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={entry.source === 'manual' ? 'secondary' : 'info'} size="sm">
          {t(`knowledgeBase.faq.source.${entry.source}`)}
        </Badge>
        <FaqEntryStatus entry={entry} onRetry={onProcess} isRetrying={isProcessing} />
      </div>
    </li>
  );
};
